import { Component, h, Host, Listen, Prop, State } from '@stencil/core';
import { IconColor } from '@wefox/icons';
import { WefoxIconSource } from '@wuic-components/wefox-icon-source';
import { IconColors } from 'components/asteroid/interfaces';
import { WefoxIconSizeClassMap, WefoxIconSizeEnum } from './wefox-icon.constants';

/**
 * @readme
 * The WefoxIcon works in conjuntion with the wefox-icon-source component.
 * The workflow to generally use icons is first provide a source of svg data provider and then consume it with this component.
 * @wuicComponentHTMLExamples
 * Default
 *
 * <wefox-icon icon="user" />
 * <wefox-icon icon="plus" />
 * <wefox-icon icon="edit" />
 *
 * icon-set="ui"
 *
 * <wefox-icon icon="user" icon-set="ui" />
 * <wefox-icon icon="plus" icon-set="ui" />
 * <wefox-icon icon="edit" icon-set="ui" />
 *
 * icon-set="flags"
 *
 * <wefox-icon icon="DE" icon-set="flags" />
 * <wefox-icon icon="FR" icon-set="flags" />
 * <wefox-icon icon="IT" icon-set="flags" />
 *
 * icon-set[CUSTOM]
 *
 * <wefox-icon-source icon-set="inlineIconDemo">
 *     <svg>
 *         <symbol height="24" id="wefox-logo" viewBox="0 0 53 14">
 *             <path d="M10.323 13.434L8.827 9.06l-1.546 4.374H4.833L1.86 6.156C1.451 5.142 1.094 4.619.771 4.315v-.169h4.266v.169c-.374.304-.425.793-.034 1.84l1.224 3.26 2.005-5.269h1.139l2.074 5.1 1.138-3.09c.374-.997.34-1.537-.034-1.841v-.169h3.841v.169c-.238.304-.578.827-.986 1.84l-2.974 7.279h-2.107zM20.944 8.266c.866 0 1.427-.405 1.427-.98 0-.54-.458-.945-1.24-.945-.884 0-1.649.608-1.972 1.452.408.254.986.473 1.785.473m4.282 2.736c0 1.199-1.342 2.668-4.214 2.668-2.923 0-5.048-2.161-5.048-4.948 0-2.87 2.362-4.796 5.048-4.796 2.736 0 4.282 1.57 4.282 3.344 0 1.47-1.41 2.6-3.688 2.6-1.155 0-2.022-.287-2.549-.557.255 1.064 1.24 1.84 2.686 1.84 1.546 0 2.413-.793 2.906-1.4.272.185.577.607.577 1.249"></path>
 *             <path fill="none" d="M31.7.076h-5.982v13.358H31.7V.076z"></path>
 *             <path d="M26.602 13.434v-.186c.459-.304.595-.81.595-1.824V6.51h-1.479V4.146h1.462a2.18 2.18 0 0 1-.544-1.452c0-1.588 1.19-2.618 2.855-2.618 1.224 0 2.108.76 2.108 1.655 0 .81-.442 1.284-1.054 1.385 0-.422-.221-.81-.68-.81-.357 0-.663.286-.663.776 0 .625.63 1.064 1.394 1.064H31.7V6.51h-1.529v4.914c0 1.013.17 1.52.595 1.824v.186h-4.164zM39.637 8.824c0-1.504-.986-2.314-2.244-2.314-1.257 0-2.243.81-2.243 2.314 0 1.469.986 2.313 2.243 2.313 1.258 0 2.244-.844 2.244-2.313m-2.244 4.846c-2.77 0-5.268-2.01-5.268-4.846 0-2.99 2.498-4.898 5.268-4.898 2.788 0 5.269 1.909 5.269 4.898 0 2.836-2.481 4.846-5.269 4.846"></path>
 *             <path d="M48.27 13.434l-.017-.17c.407-.27.305-.658-.357-1.57l-.63-.861-.577.861c-.612.878-.646 1.3-.221 1.57v.17h-4.147v-.17c.238-.27.578-.709 1.224-1.57l2.21-2.972-2.04-2.803c-.663-.912-.97-1.334-1.343-1.604v-.169h4.538v.169c-.425.27-.306.692.357 1.604l.714.98.645-.98c.595-.895.646-1.334.187-1.604v-.169h4.164v.169c-.238.27-.56.743-1.206 1.604l-2.295 3.056 1.972 2.719c.662.912 1.155 1.3 1.529 1.57v.17H48.27z"></path>
 *         </symbol>
 *     </svg>
 * </wefox-icon-source>
 * <wefox-icon icon="wefox-logo" icon-set="inlineIconDemo" style={{ width: '100px', height: '100px' }} />
 *
 * size="default"
 *
 * <wefox-icon icon="user" />
 *
 * size="small"
 *
 * <wefox-icon icon="user" size="small" />
 *
 * size="medium"
 *
 * <wefox-icon icon="user" size="medium" />
 *
 * size="large"
 *
 * <wefox-icon icon="user" size="large" />
 *
 * primary-color="primary"
 *
 * <wefox-icon icon="user" primary-color="primary" />
 *
 * secondary-color="primary"
 *
 * <wefox-icon icon="user" secondary-color="primary" />
 *
 * styling
 *
 * <wefox-icon icon="user" size="large" icon-set="icons" style={{ color: 'red', stroke: 'black' }} />
 * <wefox-icon icon="loader" size="large" icon-set="extras" style={{ color: 'red', stroke: 'blue' }} />
 * <wefox-icon
 *   size="large"
 *   icon="loader"
 *   icon-set="extras"
 *   style={{ color: 'var(--neutral-lightest)', stroke: 'var(--primary-base)' }}
 * />
 */
@Component({
  tag: 'wefox-icon',
  styleUrl: 'wefox-icon.css',
  shadow: false,
})
export class WefoxIcon {
  /**
   * Determines icon glyph to show from the currently selected iconSet
   * @wuicControlOptions ["plus","user","lock","germany","spain","france"]
   * @wuicControlType select
   * @wuicExampleValue user
   * @wuicExampleHTML
   * <wefox-icon icon="user" />
   * @wuicExample
   * el.icon="user";
   */
  @Prop() icon: string;

  /**
   * Determines the active iconSet
   * @wuicControlOptions ["icons","flags"]
   * @wuicControlType select
   * @wuicExampleHTML
   * <wefox-icon icon="user" icon-set="icons"/>
   * <wefox-icon icon="france" icon-set="flags"/>
   * @wuicExample
   * el.iconSet="flags";
   */
  @Prop() iconSet: string = 'ui';

  /**
   * Determines icon size
   * @see {@link WefoxIconSizeEnum} for further information.
   * @wuicExampleHTML
   * <wefox-icon icon="user" size="small" />
   * <wefox-icon icon="user" size="medium" />
   * <wefox-icon icon="user" size="large" />
   * @wuicExample
   * el.size="large";
   */
  @Prop() size: WefoxIconSizeEnum = WefoxIconSizeEnum.default;

  /**
   * Internally used to force a re-render
   * WARNING: iconSet set to `icons` will be deprecated.
   */
  @State() private forceRenderToggle = false;

  /**
   * Used as a primary color for the icon.
   */
  @Prop() secondaryColor: IconColors = IconColor['secondary-light'];

  /**
   * Used as a secondary color in the icon for the circle icons.
   */
  @Prop() primaryColor: IconColors = IconColor['primary-base'];

  @Listen('wefoxIconSourceIconsetLoaded', { target: 'window' })
  public handleIconsetLoaded(evnt: CustomEvent) {
    if (this.iconSet === evnt.detail) {
      this.forceRenderToggle = !this.forceRenderToggle;
    }
  }

  render() {
    const { primaryColor, secondaryColor } = this;
    const set = this.iconSet === 'icons' ? 'ui' : this.iconSet;
    const flagIconClass = set === 'flags' ? 'icon-flags' : '';

    if (WefoxIconSource.icons === undefined || WefoxIconSource.icons[set] === undefined) return <Host />;

    return (
      <Host
        class={`icon ${WefoxIconSizeClassMap[this.size]} ${flagIconClass}`}
        style={{
          '--icon-secondary-color': `var(--${secondaryColor})`,
          '--icon-primary-color': `var(--${primaryColor})`,
          color: `var(--${primaryColor})`,
        }}
      >
        <div innerHTML={WefoxIconSource.icons[set][this.icon]} />
      </Host>
    );
  }
}
